import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'



// Define the account state interface
export interface AccountState {
    user: User | null
    isLoading: boolean
    error: string | null
    isAuthenticated: boolean
}

// Initial state
const initialState: AccountState = {
    user: null,
    isLoading: false,
    error: null,
    isAuthenticated: false
}

// Async thunk to fetch user data
export const fetchUser = createAsyncThunk(
    'account/fetchUser',
    async (_, { rejectWithValue }) => {
        try {
            // Since we can't directly use hooks in thunks, we'll need to pass the API instance
            // This will be handled in the component that dispatches this action
            const response = await fetch('/user', {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch user')
            }

            const userData = await response.json()
            return userData
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch user')
        }
    }
)

// Async thunk to logout user
export const logoutUser = createAsyncThunk(
    'account/logoutUser',
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetch('/logout', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })

            if (!response.ok) {
                throw new Error('Failed to logout')
            }

            return true
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to logout')
        }
    }
)

// Create the account slice
const accountSlice = createSlice({
    name: 'account',
    initialState,
    reducers: {
        // Synchronous actions
        setUser: (state, action: PayloadAction<User | null>) => {
            state.user = action.payload
            state.isAuthenticated = !!action.payload
            state.error = null
        },
        clearUser: (state) => {
            state.user = null
            state.isAuthenticated = false
            state.error = null
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        },
        clearError: (state) => {
            state.error = null
        }
    },
    extraReducers: (builder) => {
        // Handle fetchUser async thunk
        builder
            .addCase(fetchUser.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.isLoading = false
                state.user = action.payload
                state.isAuthenticated = !!action.payload
                state.error = null
            })
            .addCase(fetchUser.rejected, (state, action) => {
                state.isLoading = false
                state.user = null
                state.isAuthenticated = false
                state.error = action.payload as string || 'Failed to fetch user'
            })

        // Handle logoutUser async thunk
        builder
            .addCase(logoutUser.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(logoutUser.fulfilled, (state) => {
                state.isLoading = false
                state.user = null
                state.isAuthenticated = false
                state.error = null
            })
            .addCase(logoutUser.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string || 'Failed to logout'
            })
    }
})

// Export actions
export const { setUser, clearUser, setError, clearError } = accountSlice.actions


// Export the reducer
export default accountSlice.reducer